// pages/MediaScreens/MediaScreens.js
//
// Media Screens — the fleet's CENTRAL PANEL. One dense, queryable table of
// every screen: the entry point to identify a device and drill into it.
// Built scale-first: the server does search / segment / status filtering,
// sorting and pagination (?q&segment&status&sort&dir&page&pageSize) and
// returns one page + facet counts — the client never holds the fleet.
//
// Glance layer on top: pulse (fleet-wide, unfiltered), agent-rollout banner.
// All view state lives in the URL, so any filtered/sorted view is a
// shareable, bookmarkable link — a console, not a dashboard.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  MonitorPlay, RefreshCw, Search, Rocket, BatteryCharging, Battery,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { ggosDeviceService } from '../../services/api';
import './MediaScreens.css';

const fmtAge = (iso) => {
  if (!iso) return 'never';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) return `${Math.round(s)}s`;
  if (s < 5400) return `${Math.round(s / 60)}m`;
  if (s < 172800) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

// Natural sort direction per column — mirrors the server's defaults so the
// second click on a header toggles to the non-default direction. For
// 'attention' the server default is asc (critical first); dir=desc flips
// only the severity term (healthy deployed first) server-side.
const SORT_DEFAULT_DIR = {
  attention: 'asc', screen: 'asc', tenant: 'asc', driver: 'asc', city: 'asc',
  lastSeen: 'desc', battery: 'asc', agent: 'desc', rides: 'desc', plays: 'desc',
};

// Column order mirrors the operator's scan: WHICH screen → is it ALIVE →
// is it POWERED → WHERE is it → running WHAT → EARNING what → any ALERTS.
// Alerts are the exception report, not the lede — severity stays scannable
// via the status dot on the Screen cell.
const COLUMNS = [
  { key: 'screen', label: 'Screen', sortable: true },
  { key: 'tenant', label: 'Driver / Tenant', sortable: true },
  { key: 'lastSeen', label: 'Connection', sortable: true },
  { key: 'battery', label: 'Battery', sortable: true },
  { key: 'city', label: 'Where', sortable: true, cls: 'ms-col-where' },
  { key: 'agent', label: 'Versions', sortable: true },
  { key: 'plays', label: 'Activity 24h', sortable: true, num: true },
  { key: 'attention', label: 'Alerts', sortable: true },
];

const AlertsCell = ({ s }) => {
  const ops = (s.reasons || []).filter(r => r.kind !== 'connectivity');
  const all = (s.reasons || []).map(r => r.text).join('\n');
  const label = ops[0]?.text || (s.status === 'ok' ? 'Healthy' : null);
  return (
    <div className="ms-cell-status" title={all || undefined}>
      {label
        ? <span className={`ms-cell-reason${ops[0] ? ` ms-cell-reason--${ops[0].severity === 'critical' ? 'critical' : 'warn'}` : ''}`}>
            {label}{ops.length > 1 ? `  +${ops.length - 1}` : ''}
          </span>
        : <span className="ms-cell-reason ms-null">—</span>}
    </div>
  );
};

// Connection: reachability ONLY — never mixed into the alerts column.
// Offline is red only when the screen is deployed (an offline spare is gray).
const ConnCell = ({ s }) => {
  if (s.online) {
    const sensing = s.lastTickAt && (Date.now() - new Date(s.lastTickAt).getTime() < 15 * 60 * 1000);
    return <span className="ms-online">● online{sensing ? ' · sensing' : ''}</span>;
  }
  const deployed = s.placed && !s.isTestDevice;
  return (
    <span className={deployed ? 'ms-conn-down' : 'ms-offline'}>
      ○ offline · {fmtAge(s.lastConnectedAt)}
    </span>
  );
};

// Agent + player versions, latest-installed (server resolves by version_code).
const VersionsCell = ({ s, target }) => (
  <div className="ms-cell-vers">
    <div>
      <span className="ms-vers-lbl">agent</span>{' '}
      {s.agentVersion
        ? <span className={`ms-agent${target && s.agentVersion !== target ? ' ms-agent--behind' : ''}`}>{s.agentVersion}</span>
        : <span className="ms-null">—</span>}
    </div>
    <div>
      <span className="ms-vers-lbl">player</span>{' '}
      {s.playerVersion ? <span className="ms-agent">{s.playerVersion}</span> : <span className="ms-null">—</span>}
    </div>
  </div>
);

// Battery: level AND power state, each explicit — "is it charging" must be
// answerable at a glance. For OFFLINE screens the data is the last report
// before going dark (cab chargers are ignition-switched, so screens go dark
// mid-charge all the time): the state is shown as "was charging/unplugged",
// stale-toned, never asserted as current.
const BatteryCell = ({ s }) => {
  if (s.battery == null) return <span className="ms-null">—</span>;
  if (!s.online) {
    const state = s.isCharging === true ? 'was charging' : s.isCharging === false ? 'was unplugged' : 'state unknown';
    return (
      <div className="ms-cell-batt ms-cell-batt--stale"
        title={`Last report before going offline${s.batterySeenAt ? ` (${fmtAge(s.batterySeenAt)} ago)` : ''}`}>
        <div className="ms-batt-val"><Battery size={13} />{s.battery}%</div>
        <div className="ms-batt-state">{state}</div>
      </div>
    );
  }
  const low = s.battery <= 20 && s.isCharging === false;
  return (
    <div className={`ms-cell-batt${s.isCharging ? ' ms-cell-batt--chg' : low ? ' ms-cell-batt--low' : ''}`}>
      <div className="ms-batt-val">
        {s.isCharging ? <BatteryCharging size={13} /> : <Battery size={13} />}{s.battery}%
      </div>
      <div className="ms-batt-state">
        {s.isCharging === true ? 'charging' : s.isCharging === false ? 'unplugged' : 'unknown'}
      </div>
    </div>
  );
};

// Driver (identifies the cab) over tenant (who it belongs to) — one column.
const WhoCell = ({ s }) => (
  <div className="ms-cell-who">
    <div className="ms-who-driver">{s.driverName || <span className="ms-null">—</span>}</div>
    {s.tenantName && <div className="ms-who-tenant">{s.tenantName}</div>}
  </div>
);

// Rides, plays, km — the 24h activity glance in one narrow column. The km
// line (GPS mobility, V261) is the independent witness: a row with km but
// zero rides is the "driving without sensing" signature the Alerts column
// flags.
const EarnCell = ({ s }) => (
  <div className="ms-cell-earn">
    <div className={s.rides24h > 0 ? 'is-live' : ''}>{s.rides24h.toLocaleString()} rides</div>
    <div className={s.plays24h > 0 ? 'is-live' : ''}>{s.plays24h.toLocaleString()} plays</div>
    {s.km24h > 0 && <div className="ms-earn-km">{Math.round(s.km24h)} km</div>}
  </div>
);

const ScreenCell = ({ s }) => (
  <div className="ms-cell-screen">
    <div className="ms-cell-name">
      <span className={`ms-dot ms-dot--${s.status}`} title={s.status} />
      {/* Real anchor: keyboard access, middle/ctrl-click, a11y tree. */}
      <Link className="ms-cell-link" to={`/media-screens/${s.thingName}`} onClick={e => e.stopPropagation()}>
        {s.plate || s.placementName || s.thingName}
      </Link>
      {s.isTestDevice && <span className="ms-tag ms-tag--test">test</span>}
      {!s.plate && s.placed && !s.isTestDevice && <span className="ms-tag">no vehicle</span>}
    </div>
    {(s.plate || s.placementName) && <div className="ms-cell-thing">{s.thingName}</div>}
  </div>
);

// ── Left filter rail: every dimension the server facets, with counts.
// Single-select per group; clicking the active row clears it. Dynamic groups
// (tenant/city/agent) sort by count, '(none)' last, capped with a More toggle.
const NONE = '(none)';
const RailRow = ({ label, count, active, onClick, tone }) => (
  <button className={`ms-rail-row${active ? ' is-active' : ''}${tone ? ` ms-rail-row--${tone}` : ''}`} onClick={onClick}>
    <span className="ms-rail-label">{label}</span>
    <span className="ms-rail-n">{count ?? 0}</span>
  </button>
);

const RailGroup = ({ title, children }) => (
  <div className="ms-rail-group">
    <div className="ms-rail-title">{title}</div>
    {children}
  </div>
);

const DynamicGroup = ({ title, facet, param, value, patch }) => {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(facet || {})
    .sort((a, b) => (a[0] === NONE) - (b[0] === NONE) || b[1] - a[1]);
  const shown = open ? entries : entries.slice(0, 8);
  if (!entries.length) return null;
  return (
    <RailGroup title={title}>
      {shown.map(([k, n]) => (
        <RailRow key={k} label={k === NONE ? 'unknown' : k} count={n} active={value === k}
          onClick={() => patch({ [param]: value === k ? null : k })} />
      ))}
      {entries.length > 8 && (
        <button className="ms-rail-more" onClick={() => setOpen(o => !o)}>
          {open ? 'less' : `+${entries.length - 8} more`}
        </button>
      )}
    </RailGroup>
  );
};

export default function MediaScreens() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qInput, setQInput] = useState(params.get('q') || '');
  const [refreshTick, setRefreshTick] = useState(0);
  const debounceRef = useRef(null);
  const searchRef = useRef(null);
  const fetchSeq = useRef(0);

  const q = params.get('q') || '';
  const segment = params.get('segment') || 'all';
  const status = params.get('status') || '';
  const conn = params.get('conn') || '';
  const batt = params.get('batt') || '';
  const tenant = params.get('tenant') || '';
  const city = params.get('city') || '';
  const agentF = params.get('agent') || '';
  const sort = params.get('sort') || 'attention';
  const dir = params.get('dir') || '';
  const anyFilter = segment !== 'all' || status || conn || batt || tenant || city || agentF || q;

  // One param mutation helper. Functional updater: immune to stale closures
  // (a pending debounce firing after a chip click must not revert the chip).
  // Filter changes reset pagination.
  const patch = useCallback((changes, { resetPage = true } = {}) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => {
        if (v === null || v === '' || v === undefined) next.delete(k); else next.set(k, String(v));
      });
      // No-op patches (e.g. the mount-time debounce re-asserting an unchanged
      // q) must not touch pagination — else a shared ?page=N link self-wipes.
      if (next.toString() === prev.toString()) return prev;
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
  }, [setParams]);

  // Debounce the search box into the URL.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => patch({ q: qInput.trim() || null }), 350);
    return () => clearTimeout(debounceRef.current);
  }, [qInput, patch]);

  // URL → search box (external navigation, e.g. sidebar link resets the view).
  // Never clobber the box while the user is typing in it.
  useEffect(() => {
    if (document.activeElement !== searchRef.current && q !== qInput.trim()) setQInput(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const queryString = params.toString();
  const freshRef = useRef(false); // set by Refresh, consumed by exactly one fetch
  useEffect(() => {
    const seq = ++fetchSeq.current;
    (async () => {
      try {
        setLoading(true);
        const qp = Object.fromEntries(new URLSearchParams(queryString));
        if (freshRef.current) { qp.fresh = '1'; freshRef.current = false; }
        const json = await ggosDeviceService.getMediaScreens(qp);
        if (seq !== fetchSeq.current) return; // a newer request superseded us
        if (!json.success) throw new Error(json.error || 'failed');
        setData(json.data);
        setError(null);
      } catch (e) {
        if (seq === fetchSeq.current) setError(e.message);
      } finally {
        if (seq === fetchSeq.current) setLoading(false);
      }
    })();
  }, [queryString, refreshTick]);

  const pulse = data?.pulse;
  const lag = pulse?.agentLag;
  const facets = data?.facets;
  const rows = useMemo(() => data?.rows || [], [data]);
  const total = data?.total || 0;
  // Pager renders from the server's ECHOED page (it clamps out-of-range
  // requests), so stale bookmarked ?page=9 displays as the real last page.
  const pageSize = data?.pageSize || 50;
  const page = data?.page || 1;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  const clickSort = (key) => {
    const isDefaultKey = key === 'attention';
    if (sort !== key) return patch({ sort: isDefaultKey ? null : key, dir: null });
    const active = dir || SORT_DEFAULT_DIR[key];
    const nextDir = active === 'asc' ? 'desc' : 'asc';
    // Toggling back to the column's natural direction clears ?dir (URL hygiene).
    patch({ sort: isDefaultKey ? null : key, dir: nextDir === SORT_DEFAULT_DIR[key] ? null : nextDir });
  };

  const rowNav = (e, s) => {
    if (e.target.closest('a,button')) return;
    if (window.getSelection && String(window.getSelection())) return; // copying text, not navigating
    navigate(`/media-screens/${s.thingName}`);
  };

  const lagVersions = lag && Object.entries(lag.versions || {})
    .sort((a, b) => b[1] - a[1]).map(([v, n]) => `${n}× ${v}`).join(', ');

  return (
    <div className="ms-page">
      <div className="ms-head">
        <MonitorPlay size={24} className="ms-head-icon" />
        <div>
          <h1 className="ms-title">Media Screens</h1>
          {pulse && <div className="ms-sub">{pulse.total} screens · {lag ? `${lag.deployed} deployed` : ''}</div>}
        </div>
        <button className="btn btn-secondary ms-refresh" onClick={() => { freshRef.current = true; setRefreshTick(t => t + 1); }} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'ms-spin' : ''} /> Refresh
        </button>
      </div>

      {pulse && (
        <div className="ms-pulse">
          <div className="ms-pulse-cell"><div className="ms-pulse-val">{pulse.online}<span className="ms-pulse-of">/{pulse.total}</span></div><div className="ms-pulse-lbl">Online</div></div>
          <div className="ms-pulse-cell"><div className="ms-pulse-val">{pulse.sensing}</div><div className="ms-pulse-lbl">Sensing now</div></div>
          <button className="ms-pulse-cell ms-pulse-cell--btn" onClick={() => patch({ status: status === 'critical' ? null : 'critical', segment: null })}>
            <div className={`ms-pulse-val${pulse.critical ? ' ms-val--crit' : ''}`}>{pulse.critical}</div><div className="ms-pulse-lbl">Critical</div>
          </button>
          <button className="ms-pulse-cell ms-pulse-cell--btn" onClick={() => patch({ status: status === 'warn' ? null : 'warn', segment: null })}>
            <div className={`ms-pulse-val${pulse.warn ? ' ms-val--warn' : ''}`}>{pulse.warn}</div><div className="ms-pulse-lbl">Watch</div>
          </button>
          <div className="ms-pulse-cell"><div className="ms-pulse-val ms-val--earn">{pulse.rides24h.toLocaleString()}</div><div className="ms-pulse-lbl">Rides 24h</div></div>
          <div className="ms-pulse-cell"><div className="ms-pulse-val ms-val--earn">{pulse.plays24h.toLocaleString()}</div><div className="ms-pulse-lbl">Plays 24h</div></div>
          {pulse.km24h != null && <div className="ms-pulse-cell"><div className="ms-pulse-val ms-val--earn">{pulse.km24h.toLocaleString()}</div><div className="ms-pulse-lbl">Km 24h</div></div>}
        </div>
      )}

      {lag && lag.behind > 0 && (
        <div className="ms-rollout">
          <Rocket size={15} />
          <span><strong>Agent rollout pending</strong> — {lag.behind} of {lag.deployed} deployed screens behind {lag.target}{lagVersions ? ` (${lagVersions})` : ''}</span>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {loading && !data && <div className="ms-loading"><RefreshCw size={18} className="ms-spin" /> Loading fleet…</div>}

      {data && (
        <div className="ms-body">
          <aside className="ms-rail">
            {anyFilter && (
              <button className="ms-rail-clear" onClick={() => { setQInput(''); patch({ segment: null, status: null, conn: null, batt: null, tenant: null, city: null, agent: null, q: null }); }}>
                × Clear all filters
              </button>
            )}
            <RailGroup title="Fleet">
              <RailRow label="All screens" count={facets?.segment.all} active={segment === 'all'} onClick={() => patch({ segment: null })} />
              <RailRow label="Deployed" count={facets?.segment.deployed} active={segment === 'deployed'} onClick={() => patch({ segment: segment === 'deployed' ? null : 'deployed' })} />
              <RailRow label="Shelf" count={facets?.segment.shelf} active={segment === 'shelf'} onClick={() => patch({ segment: segment === 'shelf' ? null : 'shelf' })} />
              <RailRow label="Test units" count={facets?.segment.test} active={segment === 'test'} onClick={() => patch({ segment: segment === 'test' ? null : 'test' })} />
            </RailGroup>
            <RailGroup title="Status">
              <RailRow label="Critical" tone="crit" count={facets?.status.critical} active={status === 'critical'} onClick={() => patch({ status: status === 'critical' ? null : 'critical' })} />
              <RailRow label="Watch" tone="warn" count={facets?.status.warn} active={status === 'warn'} onClick={() => patch({ status: status === 'warn' ? null : 'warn' })} />
              <RailRow label="Healthy" tone="ok" count={facets?.status.ok} active={status === 'ok'} onClick={() => patch({ status: status === 'ok' ? null : 'ok' })} />
            </RailGroup>
            <RailGroup title="Connection">
              <RailRow label="Online" tone="ok" count={facets?.conn.online} active={conn === 'online'} onClick={() => patch({ conn: conn === 'online' ? null : 'online' })} />
              <RailRow label="Offline" count={facets?.conn.offline} active={conn === 'offline'} onClick={() => patch({ conn: conn === 'offline' ? null : 'offline' })} />
            </RailGroup>
            <RailGroup title="Battery · online">
              <RailRow label="Charging" tone="ok" count={facets?.batt.charging} active={batt === 'charging'} onClick={() => patch({ batt: batt === 'charging' ? null : 'charging' })} />
              <RailRow label="Unplugged" count={facets?.batt.unplugged} active={batt === 'unplugged'} onClick={() => patch({ batt: batt === 'unplugged' ? null : 'unplugged' })} />
              <RailRow label="Low (≤20%)" tone="warn" count={facets?.batt.low} active={batt === 'low'} onClick={() => patch({ batt: batt === 'low' ? null : 'low' })} />
            </RailGroup>
            <DynamicGroup title="Tenant" facet={facets?.tenant} param="tenant" value={tenant} patch={patch} />
            <DynamicGroup title="City" facet={facets?.city} param="city" value={city} patch={patch} />
            <DynamicGroup title="Agent version" facet={facets?.agent} param="agent" value={agentF} patch={patch} />
          </aside>

          <div className="ms-main">
            <div className="ms-search">
              <Search size={15} />
              <input ref={searchRef} placeholder="Search plate, driver, tenant, city, neighbourhood, thing name…"
                value={qInput} onChange={e => setQInput(e.target.value)} />
              {qInput && <button className="ms-search-clear" onClick={() => setQInput('')}>×</button>}
            </div>
          <div className={`ms-tablewrap${loading ? ' ms-tablewrap--loading' : ''}`}>
            <table className="ms-table">
              <thead>
                <tr>
                  {COLUMNS.map(c => (
                    <th key={c.key} className={`${c.num ? 'is-num' : ''}${c.cls ? ` ${c.cls}` : ''}`}
                      aria-sort={sort === c.key ? ((dir || SORT_DEFAULT_DIR[c.key]) === 'asc' ? 'ascending' : 'descending') : undefined}>
                      <button className="ms-th" onClick={() => clickSort(c.key)} aria-label={`Sort by ${c.label}`}>
                        {c.label}
                        {sort === c.key
                          ? ((dir || SORT_DEFAULT_DIR[c.key]) === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)
                          : <ArrowUpDown size={10} className="ms-th-hint" />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.thingName} onClick={e => rowNav(e, s)}>
                    <td><ScreenCell s={s} /></td>
                    <td className="ms-cell-mid"><WhoCell s={s} /></td>
                    <td className="ms-cell-mid"><ConnCell s={s} /></td>
                    <td className="ms-cell-mid"><BatteryCell s={s} /></td>
                    <td className="ms-cell-mid ms-col-where">{[s.neighborhood, s.city].filter(Boolean).join(', ') || <span className="ms-null">—</span>}</td>
                    <td className="ms-cell-mid"><VersionsCell s={s} target={lag?.target} /></td>
                    <td className="is-num"><EarnCell s={s} /></td>
                    <td><AlertsCell s={s} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <div className="ms-empty">No screens match this view.</div>}
          </div>

          <div className="ms-pager">
            <span className="ms-pager-info">{from}–{to} of {total.toLocaleString()}</span>
            <div className="ms-pager-ctl">
              <select value={pageSize} onChange={e => patch({ pageSize: e.target.value === '50' ? null : e.target.value })}>
                {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <button disabled={page <= 1} onClick={() => patch({ page: page - 1 <= 1 ? null : page - 1 }, { resetPage: false })} aria-label="Previous page"><ChevronLeft size={15} /></button>
              <span className="ms-pager-page">{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => patch({ page: page + 1 }, { resetPage: false })} aria-label="Next page"><ChevronRight size={15} /></button>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
